import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AssignChecklists from "../components/AssignChecklists";
import AssignedChecklists from "../components/AssignedChecklists";
import ChecklistModal from "../components/ChecklistModal";
import CustomerHistoryModal from "../components/CustomerHistoryModal";
import MessageBox from "../components/helpers/MessageBox";
import MessageBoxButton from "../components/helpers/MessageBoxButton";
import JobLogForm from "../components/JobLogForm";
import {
  compareDatesOnly,
  formatAUDate,
  formatAUTime,
  formatMobile,
  getGameConsoleId,
  minutesToHoursMinutesSeconds,
} from "../global-client";
import { getChecklists } from "../redux/actions/checklistActions";
import {
  addJobLogEntry,
  deleteJobLogEntry,
  flagJobComplete,
  flagJobIrreparable,
  flagJobPickedup,
  flagJobReviewRequested,
  getJob,
  setProgressStatus,
} from "../redux/actions/jobActions";
import JobLogRow from "./screenComponents/JobLogRow";
import MessageRow from "./screenComponents/MessageRow";
import PostcodeModal from "./screenComponents/PostcodeModal";

const JobScreen = () => {
  const [liveDuration, setLiveDuration] = useState(0);
  const [editingLog, setEditingLog] = useState(null);
  const [postcodeModalOpen, setPostcodeModalOpen] = useState(false);
  const [customerHistoryModalOpen, setCustomerHistoryModalOpen] = useState(false);
  const [assignedChecklists, setAssignedChecklists] = useState([]);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const [assignChecklistsModalOpen, setAssignChecklistsModalOpen] = useState(false);
  const [showHiddenLogs, setShowHiddenLogs] = useState(false);

  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isJobLogModalOpen, onOpen: onJobLogModalOpen, onClose: onJobLogModalClose } = useDisclosure();
  const { num } = useParams();
  const dispatch = useDispatch();
  const { error, loading, job, jobLogs, messages, progressStatus, totalDuration, lastStartTime } = useSelector(
    (state) => state.job
  );
  const { checklists } = useSelector((state) => state.checklist);
  const toast = useToast();
  const isInitialLoad = loading && !job;
  const isRefreshing = loading && !!job;
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const sectionColor = useColorModeValue("gray.100", "gray.700");
  const lightSectionColor = useColorModeValue("gray.50", "gray.600");
  const hoverColor = useColorModeValue("blue.600", "blue.300");

  const showToast = useCallback(
    (title, description, status = "success") => {
      toast({
        title,
        description,
        status,
        duration: 3000,
        isClosable: true,
      });
    },
    [toast]
  );

  const handleEditLog = useCallback(
    (log) => {
      setEditingLog(log);
      onJobLogModalOpen();
    },
    [onJobLogModalOpen]
  );

  const handleDeleteLog = useCallback(
    async (log) => {
      if (!log?._id) return;
      const confirmed = window.confirm("Are you sure you want to delete this job log entry?");
      if (!confirmed) return;

      try {
        await dispatch(deleteJobLogEntry(log._id));
        showToast("Job Log Deleted", "The job log entry was deleted.");
      } catch (error) {
        showToast("Error", "Failed to delete job log entry.", "error");
      }
    },
    [dispatch, showToast]
  );

  useEffect(() => {
    dispatch(getJob(num));
    dispatch(getChecklists());
  }, [dispatch, num]);

  useEffect(() => {
    if (job && job.JOB_ID) {
      dispatch({ type: "jobs/setLastViewedJobNum", payload: job.JOB_NO });
    }
  }, [dispatch, job]);

  const loadAssignedChecklists = useCallback(
    async (withToast = true) => {
      if (!job?.JOB_NO) return;
      try {
        const { data } = await axios.get(`/api/checklists/job/${job.JOB_NO}`);
        setAssignedChecklists(data.checklists || []);
      } catch (error) {
        console.error("Failed to fetch assigned checklists:", error);
        if (withToast) showToast("Error", "Failed to load assigned checklists", "error");
      }
    },
    [job?.JOB_NO, showToast]
  );

  useEffect(() => {
    loadAssignedChecklists();
  }, [loadAssignedChecklists]);

  useEffect(() => {
    if (!job?.JOB_NO) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch(getJob(num));
        loadAssignedChecklists(false);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [dispatch, num, job?.JOB_NO, loadAssignedChecklists]);

  useEffect(() => {
    let timer;
    if (progressStatus === "Started" && lastStartTime) {
      timer = setInterval(() => {
        const now = new Date();
        const lastStart = new Date(lastStartTime);
        const diffMs = now - lastStart;
        setLiveDuration(diffMs / 1000 / 60);
      }, 1000);
    } else {
      setLiveDuration(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [progressStatus, lastStartTime]);

  const handleFlagChange = (flagType, currentValue) => {
    const isToggleOff = currentValue === 1;
    switch (flagType) {
      case "Complete":
        dispatch(flagJobComplete(job.JOB_ID, isToggleOff));
        break;
      case "Picked Up":
        dispatch(flagJobPickedup(job.JOB_ID, isToggleOff));
        break;
      case "Not Repairable":
        dispatch(flagJobIrreparable(job.JOB_ID, isToggleOff));
        break;
      case "Review Requested":
        dispatch(flagJobReviewRequested(job.JOB_ID, isToggleOff));
        break;
      default:
        break;
    }
    const action = isToggleOff ? "unmarked" : "marked";
    showToast("Job Updated", `Job has been ${action} as ${flagType}.`);
  };

  const handleJobProgress = (status, title) => {
    var descTail = "";
    switch (status) {
      case "Started":
        descTail = " started.";
        break;
      case "Paused":
        descTail = " paused.";
        break;
      case "Finished":
        descTail = " finished.";
        break;
      default:
        descTail = " updated.";
    }
    const jobLogEntry = {
      jobId: job.JOB_ID,
      jobNo: job.JOB_NO,
      status: status,
      title: title,
      description: `The job has been${descTail}`,
      date: new Date().toISOString(),
      hours: 0,
      cost: 0,
      billable: false,
      photos: [],
    };
    dispatch(addJobLogEntry(jobLogEntry));
    dispatch(setProgressStatus(job.JOB_ID, status));
    showToast("Job Log Updated", `Job has been marked as ${status}.`);
    if (status === "Finished") {
      onOpen();
    }
  };

  const handleSendSMSTo = (toNumber, toFName, toSName) => {
    navigate(`/send-sms/`, {
      state: {
        toNumber: toNumber,
        fName: toFName,
        sName: toSName,
        jobNo: job.JOB_NO,
        jobId: job.JOB_ID,
        custId: job.CUSTOMER_ID,
        itemName: job.CONSOLE_NAME,
        total: job.TOTAL,
        balance: job.BALANCE,
        eta: job.ETA,
      },
    });
  };

  return (
    <>
      {isInitialLoad ? (
        <Box
          maxW={{ base: "3xl", md: "5xl" }}
          mx='auto'
          px={{ base: "2", md: "6", lg: "2" }}
          py={{ base: "6", md: "8", lg: "12" }}
          w='95vw'
          textAlign='center'
        >
          <Text fontSize='lg' mb='6'>
            Loading job details...
          </Text>
        </Box>
      ) : error ? (
        <Box
          maxW={{ base: "3xl", md: "5xl" }}
          mx='auto'
          px={{ base: "2", md: "6", lg: "2" }}
          py={{ base: "6", md: "8", lg: "12" }}
          w='95vw'
        >
          <Alert status='error'>
            <AlertIcon />
            <AlertTitle>We are sorry!!!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Box>
      ) : (
        job && (
          <Box
            maxW={{ base: "3xl", md: "5xl" }}
            mx='auto'
            px={{ base: "2", md: "6", lg: "2" }}
            py={{ base: "6", md: "8", lg: "12" }}
            w='95vw'
          >
            <HStack spacing={3} mb={2}>
              <Button variant='link' size='sm' onClick={() => window.history.back()}>
                &larr; Back
              </Button>
              <Button variant='link' size='sm' onClick={() => dispatch(getJob(num))}>
                &#x21bb; Refresh
              </Button>
              {isRefreshing && (
                <Text fontSize='xs' color='gray.500'>
                  Refreshing...
                </Text>
              )}
            </HStack>
            <Heading size='md' align='center'>
              <HStack justify='center' mb='2'>
                {job.BOOKING ? <Text fontWeight='normal'>Booking</Text> : <Text fontWeight='normal'>Job</Text>}
                <Text fontSize='xl'>#{job.JOB_NO}</Text>
              </HStack>
            </Heading>
            <MessageBox
              isOpen={isOpen}
              onClose={onClose}
              title='Would you like to Send SMS?'
              description={`Do you want to send an SMS to ${
                job.PREFERRED_CONTACT2 === 0 ? job.CUST_FNAME : job.CONT_FNAME2
              } ${job.PREFERRED_CONTACT2 === 0 ? job.CUST_SNAME : job.CONT_SNAME2} at ${formatMobile(
                job.PREFERRED_CONTACT2 === 0 ? job.CUST_CONT1 : job.CUST_CONT2
              )}?`}
              confirmText='Yes, Send SMS'
              cancelText={`No, I'll do it later`}
              colorScheme='cyan'
              onConfirm={() => {
                onClose();
                if (job.PREFERRED_CONTACT2 === 0) {
                  handleSendSMSTo(job.CUST_CONT1, job.CUST_FNAME, job.CUST_SNAME);
                } else {
                  handleSendSMSTo(job.CUST_CONT2, job.CONT_FNAME2, job.CONT_SNAME2);
                }
              }}
              onCancel={() => {
                onClose();
                navigate("/jobs/", { refresh: true });
              }}
            />

            <Flex
              //borderWidth='1px'
              borderRadius='lg'
              //p='2'
              mb='2'
              justify='space-between'
              alignItems='stretch'
              direction={{ base: "column", md: "row" }}
              gap='4'
            >
              <Flex
                flex='1'
                p='2'
                direction='row'
                borderWidth='1px'
                borderRadius='md'
                bg={lightSectionColor}
                alignItems='start'
              >
                <Flex p='2' direction='column' justify='space-between' alignItems='start' alignContent='center'>
                  <HStack mb={2}>
                    <Text fontSize='sm' fontWeight='normal' minW={job.BOOKING ? "100px" : "50px"} fontStyle='italic'>
                      Taken on:
                    </Text>
                    <Text
                      colorScheme={
                        compareDatesOnly(new Date(job.ETA), today) <= 0
                          ? "red"
                          : compareDatesOnly(new Date(job.ETA), tomorrow) === 0
                          ? "blue"
                          : "yellow"
                      }
                      fontSize='sm'
                    >
                      {formatAUDate(job.JOB_DATE, { weekday: true })} - {formatAUTime(job.JOB_DATE)}
                    </Text>
                  </HStack>
                  <HStack>
                    <Text fontSize='md' fontWeight='normal' minW={job.BOOKING ? "100px" : "50px"} fontStyle='italic'>
                      {!job.BOOKING ? "ETA:" : "Booked for:"}
                    </Text>
                    <Badge
                      // colorScheme={
                      //   compareDatesOnly(new Date(job.ETA), today) <= 0
                      //     ? "red"
                      //     : compareDatesOnly(new Date(job.ETA), tomorrow) === 0
                      //     ? "blue"
                      //     : "yellow"
                      // }
                      fontSize='md'
                      rounded='md'
                    >
                      {formatAUDate(job.ETA, { weekday: true })} - {formatAUTime(job.ETA)}
                    </Badge>
                  </HStack>
                  <HStack mt='2'>
                    <Badge fontSize='md' rounded='md'>
                      {job.IRREPARABLE ? "NOT REPAIRABLE" : job.COMPLETE ? "COMPLETE" : ""}
                    </Badge>
                    <Badge fontSize='md' rounded='md'>
                      {job.PICKEDUP ? "PICKED-UP" : ""}
                    </Badge>
                  </HStack>
                </Flex>
              </Flex>
              {!job.BOOKING && (
                <Flex flex='2' direction={{ base: "column", md: "row" }}>
                  <Flex
                    flex='1'
                    p='2'
                    //ml={{ base: "0", md: "4" }}
                    direction='column'
                    gap='2'
                    alignItems='center'
                    bg={lightSectionColor}
                    borderWidth='1px'
                    borderRadius='md'
                  >
                    <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                      Progress Status:
                    </Text>
                    <HStack>
                      <Badge fontSize='md' rounded='md'>
                        Job {progressStatus === "Started" ? "In Progress" : progressStatus}
                      </Badge>
                      {/* <Text>{job.PROGRESS_STATUS ? job.PROGRESS_STATUS : "???"}</Text> */}
                    </HStack>
                    <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                      Duration:{" "}
                      <Text as='span' fontWeight='bold'>
                        {minutesToHoursMinutesSeconds(totalDuration + liveDuration)}
                      </Text>
                    </Text>
                  </Flex>
                  <Flex
                    flex='1'
                    px='4'
                    ml={{ base: "0", md: "4" }}
                    direction='column'
                    gap='2'
                    bg={lightSectionColor}
                    borderWidth='1px'
                    borderRadius='md'
                  >
                    <Text mt='2' fontSize='md' fontWeight='normal' fontStyle='italic'>
                      Update Job Progress:
                    </Text>
                    <HStack spacing='2' alignSelf='start'>
                      <Button
                        onClick={() => {
                          handleJobProgress("Started", "Job Started");
                        }}
                        size='sm'
                        colorScheme='green'
                        disabled={progressStatus === "Started"}
                      >
                        {progressStatus === "Finished" || progressStatus === "Paused" ? "Resume Job" : "Start Job"}
                      </Button>
                      {/* <MessageBoxButton
                        buttonText={
                          progressStatus === "Finished" || progressStatus === "Paused" ? "Resume Job" : "Start Job"
                        }
                        title='Confirm Start Job'
                        description='Are you sure you want to mark this job as Started?'
                        confirmText='Yes, Start Job'
                        cancelText='Cancel'
                        colorScheme='green'
                        onConfirm={() => {
                          handleJobProgress("Started", "Job Started");
                        }}
                        buttonProps={{
                          size: "sm",
                          //disabled: job.COMPLETE,
                          colorScheme: "green",
                          disabled: progressStatus === "Started",
                        }}
                      /> */}
                      <Button
                        colorScheme='blue'
                        size='sm'
                        disabled={progressStatus !== "Started"}
                        onClick={() => {
                          handleJobProgress("Paused", "Job Paused");
                        }}
                      >
                        Pause Job
                      </Button>
                      {/* <MessageBoxButton
                        buttonText='Pause Job'
                        title='Confirm Pause Job'
                        description='Are you sure you want to mark this job as Paused?'
                        confirmText='Yes, Pause Job'
                        cancelText='Cancel'
                        colorScheme='blue'
                        onConfirm={() => {
                          handleJobProgress("Paused", "Job Paused");
                        }}
                        buttonProps={{
                          size: "sm",
                          //disabled: job.COMPLETE,
                          colorScheme: "blue",
                          disabled: progressStatus !== "Started",
                        }}
                      /> */}

                      <Button
                        colorScheme='red'
                        size='sm'
                        onClick={() => {
                          handleJobProgress("Finished", "Job Finished");
                        }}
                        disabled={progressStatus === "Finished"}
                      >
                        Finish Job
                      </Button>
                      {/* <MessageBoxButton
                        buttonText='Finish Job'
                        title='Confirm Finish Job'
                        description='Are you sure you want to mark this job as Finished?'
                        confirmText='Yes, Finish Job'
                        cancelText='Cancel'
                        colorScheme='red'
                        onConfirm={() => {
                          handleJobProgress("Finished", "Job Finished");
                        }}
                        buttonProps={{
                          size: "sm",
                          //disabled: job.IRREPARABLE,
                          colorScheme: "red",
                        }}
                      /> */}
                    </HStack>
                    <HStack spacing='2' mt='1' mb='2' alignSelf='center'>
                      <Button
                        size='sm'
                        //colorScheme='blackAlpha'
                        onClick={() => {
                          setEditingLog(null);
                          onJobLogModalOpen();
                        }}
                      >
                        Add Log Entry
                      </Button>
                    </HStack>
                  </Flex>
                </Flex>
              )}
            </Flex>
            <Flex direction={{ base: "column", md: "row" }} justify='space-start' mb='4' gap={4}>
              <Box pr='8' pl='4' py='4' bg={sectionColor} w={{ base: "100%", md: "50%" }} borderRadius='md'>
                <Flex justify='start' alignItems='center' alignContent='center'>
                  <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                    Customer Name:
                  </Text>
                  <Text
                    fontSize='md'
                    fontWeight='semibold'
                    cursor='pointer'
                    textDecoration='underline'
                    textUnderlineOffset='2px'
                    onClick={() => setCustomerHistoryModalOpen(true)}
                    _hover={{ color: hoverColor }}
                  >
                    {job.CUST_FNAME + " " + job.CUST_SNAME}
                  </Text>
                  <Text
                    fontSize='md'
                    fontWeight='semibold'
                    textDecoration='underline'
                    textUnderlineOffset='2px'
                    onClick={() => setPostcodeModalOpen(true)}
                    cursor='pointer'
                    ml='2'
                  >
                    ({job.CUST_POSTCODE})
                  </Text>
                </Flex>

                <Flex justify='start' alignItems='center' alignContent='center'>
                  <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                    Mobile Number:
                  </Text>
                  <Text fontSize='md' fontWeight='semibold'>
                    {formatMobile(job.CUST_CONT1)}
                  </Text>
                  {job.CUST_CONT1 && (
                    <Button
                      variant={job.PREFERRED_CONTACT2 === 0 ? "solid" : "outline"}
                      size='sm'
                      colorScheme='cyan'
                      ml='2'
                      h='28px'
                      mt='1'
                      onClick={() => handleSendSMSTo(job.CUST_CONT1, job.CUST_FNAME, job.CUST_SNAME)}
                    >
                      Send SMS
                    </Button>
                  )}
                </Flex>
                {job.CUST_EMAIL && (
                  <Flex justify='start' alignItems='center' alignContent='center'>
                    <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                      Email:
                    </Text>
                    <Text fontSize='md' fontWeight='semibold'>
                      {job.CUST_EMAIL}
                    </Text>

                    {/* <Button
                      variant='outline'
                      size='sm'
                      colorScheme='cyan'
                      ml='2'
                      h='28px'
                      mt='1'
                      //onClick={() => handleSendEmailTo(job.CUST_EMAIL, job.CUST_FNAME, job.CUST_SNAME)}
                    >
                      Send Email
                    </Button> */}
                  </Flex>
                )}

                {job.CONT_FNAME2 && (
                  <Flex justify='start' alignItems='center' alignContent='center'>
                    <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                      Contact Name:
                    </Text>
                    <Text fontSize='md' fontWeight='semibold'>
                      {job.CONT_FNAME2 + " " + job.CONT_SNAME2}
                    </Text>
                  </Flex>
                )}
                {job.CUST_CONT2 && (
                  <Flex justify='start' alignItems='center' alignContent='center'>
                    <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                      Contact Number:
                    </Text>
                    <Text fontSize='md' fontWeight='semibold'>
                      {formatMobile(job.CUST_CONT2)}
                    </Text>
                    <Button
                      variant={job.PREFERRED_CONTACT2 === 1 ? "solid" : "outline"}
                      size='sm'
                      colorScheme='cyan'
                      ml='2'
                      h='28px'
                      mt='1'
                      onClick={() => handleSendSMSTo(job.CUST_CONT2, job.CONT_FNAME2, job.CONT_SNAME2)}
                    >
                      Send SMS
                    </Button>
                  </Flex>
                )}
                <Flex justify='start' alignItems='center' alignContent='center'>
                  <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                    Postcode:
                  </Text>
                  <Text
                    fontSize='md'
                    fontWeight='semibold'
                    textDecoration='underline'
                    textUnderlineOffset='2px'
                    onClick={() => setPostcodeModalOpen(true)}
                    cursor='pointer'
                  >
                    {job.CUST_POSTCODE}
                  </Text>
                  <PostcodeModal
                    isOpen={postcodeModalOpen}
                    onClose={() => setPostcodeModalOpen(false)}
                    postcode={job.CUST_POSTCODE}
                  />
                  <CustomerHistoryModal
                    isOpen={customerHistoryModalOpen}
                    onClose={() => setCustomerHistoryModalOpen(false)}
                    customerName={job.CUST_FNAME + " " + job.CUST_SNAME}
                    customerId={job.CUSTOMER_ID}
                  />
                </Flex>
              </Box>
              <Box pr='8' pl='4' py='4' bg={sectionColor} w={{ base: "100%", md: "50%" }} borderRadius='md'>
                <Flex justify='start' alignItems='center' alignContent='start'></Flex>
                <Flex justify='start' alignItems='center' alignContent='center'>
                  <Text fontSize='md' fontWeight='normal' minW='80px' fontStyle='italic'>
                    Job:
                  </Text>
                  <Text fontSize='md' fontWeight='semibold'>
                    {job.JOB_NAME1}
                  </Text>
                </Flex>
                <Flex justify='start' alignItems='center' alignContent='center'>
                  <Text fontSize='md' fontWeight='normal' minW='80px' fontStyle='italic'></Text>
                  <Text fontSize='md' fontWeight='semibold'>
                    {job.JOB_NAME2}
                  </Text>
                </Flex>
                {job.JOB_COMMENTS && (
                  <Flex justify='start' alignItems='start' alignContent='center'>
                    <Text fontSize='md' fontWeight='normal' minW='80px' fontStyle='italic'>
                      Notes:
                    </Text>
                    <Text fontSize='md' fontWeight='normal'>
                      <span dangerouslySetInnerHTML={{ __html: job.JOB_COMMENTS.replace(/\n/g, "<br />") }} />
                    </Text>
                  </Flex>
                )}
              </Box>
            </Flex>
            {/* <Tabs variant='enclosed' colorScheme='cyan' defaultIndex={job.BOOKING ? 0 : 1}> */}
            <Tabs variant='enclosed' colorScheme='cyan' defaultIndex={0}>
              {/* List of tabs */}
              <TabList>
                <Tab>Details</Tab>
                {job.BOOKING ? null : (
                  <>
                    <Tab>Status</Tab>
                    <Tab>Log</Tab>
                  </>
                )}
                <Tab>Messages</Tab>
                <Tab>
                  Check Lists
                  {assignedChecklists.length > 0 && (
                    <Badge ml={2} colorScheme='green' fontSize='0.8em' px={2} py={1} borderRadius='full'>
                      {assignedChecklists.length}
                    </Badge>
                  )}
                </Tab>
              </TabList>

              {/* Content of tabs */}
              <TabPanels>
                {/* Details Content Goes Here */}
                <TabPanel>
                  <Box mx='-4' mt={-3}>
                    <Box borderWidth='1px' borderRadius='lg' p='2' mb='1'>
                      <Flex justify='start' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                          Item:
                        </Text>
                        <Text fontSize='md' fontWeight='semibold'>
                          {job.CONSOLE_NAME}{" "}
                          <span style={{ fontStyle: "italic", fontWeight: "normal", fontSize: "smaller" }}>
                            {job
                              ? `(${getGameConsoleId(
                                  job.CONSOLE_NAME,
                                  job.CONSOLE_MODEL,
                                  job.CONSOLE_DRIVE === "Y"
                                    ? "Disc Drive Version"
                                    : job.CONSOLE_DRIVE === "D"
                                    ? "Digital Version"
                                    : ""
                                )})`
                              : "(N/A)"}
                          </span>
                        </Text>
                      </Flex>
                      <Flex justify='start' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                          Model:
                        </Text>
                        <Text fontSize='md' fontWeight='semibold'>
                          {job.CONSOLE_MODEL}
                        </Text>
                      </Flex>
                      {job.CONSOLE_COLOUR && (
                        <Flex justify='start' alignItems='center' alignContent='center'>
                          <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                            Colour:
                          </Text>
                          <Text fontSize='md' fontWeight='semibold'>
                            {job.CONSOLE_COLOUR}
                          </Text>
                        </Flex>
                      )}
                      {job.CONSOLE_DRIVE === "Y" ? (
                        <Flex justify='start' alignItems='center' alignContent='center'>
                          <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'></Text>
                          <Text fontSize='md' fontWeight='semibold'>
                            Disc Drive Version
                          </Text>
                        </Flex>
                      ) : job.CONSOLE_DRIVE === "D" ? (
                        <Flex justify='start' alignItems='center' alignContent='center'>
                          <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'></Text>
                          <Text fontSize='md' fontWeight='semibold'>
                            Digital Version
                          </Text>
                        </Flex>
                      ) : null}
                      <Flex justify='start' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                          Accessories:
                        </Text>
                        <Text fontSize='md' fontWeight='semibold'>
                          {job.CONSOLE_ACCESS || "N/A"}
                        </Text>
                      </Flex>
                      <Flex justify='start' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' minW='130px' fontStyle='italic'>
                          Serial Number:
                        </Text>
                        <Text fontSize='md' fontWeight='semibold'>
                          {job.CONSOLE_SERIAL || "N/A"}
                        </Text>
                      </Flex>
                    </Box>
                    {/**
                    
                    {/**
                     *
                     * Pricing Details *
                     *
                     **/}
                    <Flex direction='column' borderWidth='1px' borderRadius='lg' p='2' mb='1'>
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                          Price:
                        </Text>
                        <Text fontSize='md' fontWeight='normal' minW='80px' textAlign='right'>
                          {job.PRICE ? `$${job.PRICE.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                          Extra:
                        </Text>
                        <Text fontSize='md' fontWeight='normal' minW='80px' textAlign='right'>
                          {job.EXTRA ? `$${job.EXTRA.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                          Discount:
                        </Text>
                        <Text fontSize='md' fontWeight='normal' minW='80px' textAlign='right'>
                          {job.DISCOUNT ? `-$${job.DISCOUNT.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                      <Divider my='2' width='150px' alignSelf='end' />
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='semibold' fontStyle='italic'>
                          Total:
                        </Text>
                        <Text fontSize='md' fontWeight='semibold' minW='80px' textAlign='right'>
                          {job.TOTAL ? `$${job.TOTAL.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='normal' fontStyle='italic'>
                          Paid:
                        </Text>
                        <Text fontSize='md' fontWeight='normal' minW='80px' textAlign='right'>
                          {job.PAID ? `-$${job.PAID.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                      <Divider my='2' width='150px' alignSelf='end' />
                      <Flex justify='end' alignItems='center' alignContent='center'>
                        <Text fontSize='md' fontWeight='bold' fontStyle='italic'>
                          Balance:
                        </Text>
                        <Text fontSize='md' fontWeight='black' minW='80px' textAlign='right'>
                          {job.BALANCE ? `$${job.BALANCE.toFixed(2)}` : "$0.00"}
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                </TabPanel>

                {/* Progress Content Goes Here */}
                {!job.BOOKING && (
                  <TabPanel>
                    <Box mx='-4' mt={-3}>
                      <Flex direction='column' justify='start' borderWidth='1px' borderRadius='lg' p='2' mb='2'>
                        <HStack mb='2' justify='start' spacing='4' alignSelf='start'>
                          <Text fontSize='sm' fontWeight='semibold' alignSelf='start'>
                            Current Job Status:
                          </Text>
                          <HStack>
                            <Badge
                              // colorScheme={
                              //   compareDatesOnly(new Date(job.ETA), today) <= 0
                              //     ? "red"
                              //     : compareDatesOnly(new Date(job.ETA), tomorrow) === 0
                              //     ? "blue"
                              //     : "yellow"
                              // }
                              fontSize='md'
                              rounded='md'
                            >
                              {job.IRREPARABLE ? "NOT REPAIRABLE" : job.COMPLETE ? "COMPLETE" : ""}
                            </Badge>
                            <Badge fontSize='md' rounded='md'>
                              {job.PICKEDUP ? "PICKED-UP" : ""}
                            </Badge>
                          </HStack>
                        </HStack>
                        <VStack mb='2' justify='start' spacing='4' alignSelf='start'>
                          <Text fontSize='sm' fontWeight='semibold' alignSelf='start'>
                            Update Job Status:
                          </Text>
                          <HStack spacing='2' ml={{ base: "0", md: "4" }} flexWrap='wrap' rowGap='2'>
                            <MessageBoxButton
                              buttonText={job.COMPLETE ? 'Unmark Complete' : 'Mark Complete'}
                              title={job.COMPLETE ? 'Confirm Unmark Complete' : 'Confirm Complete'}
                              description={job.COMPLETE ? 'Are you sure you want to unmark this job as Complete?' : 'Are you sure you want to mark this job as Complete?'}
                              confirmText={job.COMPLETE ? 'Yes, Unmark' : 'Yes, Complete'}
                              cancelText='Cancel'
                              colorScheme='green'
                              onConfirm={() => {
                                handleFlagChange("Complete", job.COMPLETE);
                              }}
                              buttonProps={{
                                variant: "outline",
                                size: "sm",
                                colorScheme: "green",
                              }}
                            />
                            <MessageBoxButton
                              buttonText={job.PICKEDUP ? 'Unmark Picked Up' : 'Mark Picked Up'}
                              title={job.PICKEDUP ? 'Confirm Unmark Picked Up' : 'Confirm Picked Up'}
                              description={job.PICKEDUP ? 'Are you sure you want to unmark this job as Picked Up?' : 'Are you sure you want to mark this job as Picked Up?'}
                              confirmText={job.PICKEDUP ? 'Yes, Unmark' : 'Yes, Picked Up'}
                              cancelText='Cancel'
                              colorScheme='blue'
                              onConfirm={() => {
                                handleFlagChange("Picked Up", job.PICKEDUP);
                              }}
                              buttonProps={{
                                variant: "outline",
                                size: "sm",
                                colorScheme: "blue",
                              }}
                            />
                            <MessageBoxButton
                              buttonText={job.IRREPARABLE ? 'Mark Repairable' : 'Mark Not Repairable'}
                              title={job.IRREPARABLE ? 'Confirm Mark Repairable' : 'Confirm Not Repairable'}
                              description={job.IRREPARABLE ? 'Are you sure you want to mark this job as Repairable?' : 'Are you sure you want to mark this job as Not Repairable?'}
                              confirmText={job.IRREPARABLE ? 'Yes, Repairable' : 'Yes, Not Repairable'}
                              cancelText='Cancel'
                              colorScheme='red'
                              onConfirm={() => {
                                handleFlagChange("Not Repairable", job.IRREPARABLE);
                              }}
                              buttonProps={{
                                variant: "outline",
                                size: "sm",
                                colorScheme: "red",
                              }}
                            />
                            {job.PICKEDUP && (
                            <MessageBoxButton
                              buttonText={job.REVIEW_REQUESTED ? 'Request Review' : 'Do not Request Review'}
                              title={job.REVIEW_REQUESTED ? 'Confirm Request Review' : 'Confirm Review not to be Requested'}
                              description={job.REVIEW_REQUESTED ? 'Are you sure you want to request review for this job?' : 'Are you sure you don\'t want to request review for this job?'}
                              confirmText={job.REVIEW_REQUESTED ? 'Yes, Request Review' : 'Yes, Do Not Request Review'}
                              cancelText='Cancel'
                              colorScheme='red'
                              onConfirm={() => {
                                handleFlagChange("Review Requested", job.REVIEW_REQUESTED);
                              }}
                              buttonProps={{
                                variant: "outline",
                                size: "sm",
                                colorScheme: "red",
                              }}
                            />
                            )}
                          </HStack>
                          <Divider />
                        </VStack>
                      </Flex>
                    </Box>
                  </TabPanel>
                )}

                {/* Log Content Goes Here */}
                {!job.BOOKING && (
                  <TabPanel>
                    <Flex direction='column' justify='start' p='2' mb='4' mx='-4'>
                      <HStack mb='2' justify='flex-end'>
                        {jobLogs && jobLogs.some((log) => log.status === "Hidden") && (
                          <Button onClick={() => setShowHiddenLogs(!showHiddenLogs)} variant='ghost' size='sm' mt='1'>
                            {showHiddenLogs ? <ViewIcon /> : <ViewOffIcon />}
                          </Button>
                        )}
                      </HStack>
                    </Flex>
                    <Box mx='-4' mt={-1}>
                      <Flex
                        direction='column'
                        borderWidth='1px'
                        borderRadius='lg'
                        p='4'
                        mb='2'
                        fontFamily='sans-serif'
                        fontSize='xs'
                      >
                        {jobLogs && jobLogs.length > 0 ? (
                          <>
                            <JobLogRow
                              key={"header"}
                              status='Status'
                              title='Title'
                              description='Description'
                              date='Date'
                              time='Time'
                              index={0}
                              header
                            />
                            {jobLogs
                              .filter((log) => showHiddenLogs || log.status !== "Hidden")
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((log) => (
                                <JobLogRow
                                  key={log._id}
                                  status={log.status}
                                  title={log.title}
                                  description={log.description}
                                  date={log.date && formatAUDate(log.date)}
                                  time={log.date && formatAUTime(log.date)}
                                  photos={log.photos || []}
                                  photoCount={log.photoCount || 0}
                                  log={log}
                                  onEdit={handleEditLog}
                                  onDelete={handleDeleteLog}
                                  index={jobLogs
                                    .filter((log) => showHiddenLogs || log.status !== "Hidden")
                                    .indexOf(log)}
                                />
                              ))}
                          </>
                        ) : (
                          <Text textAlign='center'>No job logs found.</Text>
                        )}
                      </Flex>
                    </Box>
                  </TabPanel>
                )}

                {/* Messages Content Goes Here */}
                <TabPanel>
                  <Box mx='-4' mt={1}>
                    {messages && messages.length > 0 ? (
                      <>
                        {messages
                          .slice()
                          .reverse()
                          .map((msg, index) => (
                            <MessageRow
                              key={msg.SMSLOG_ID}
                              text={msg.TEXT}
                              direction={msg.DIRECTION}
                              toNumber={formatMobile(msg.TO_NUMBER)}
                              fromNumber={formatMobile(msg.FROM_NUMBER)}
                              date={msg.DT_STAMP && formatAUDate(msg.DT_STAMP)}
                              time={msg.DT_STAMP && formatAUTime(msg.DT_STAMP)}
                              index={index}
                            />
                          ))}
                      </>
                    ) : (
                      <Text textAlign='center'>No messages found.</Text>
                    )}
                  </Box>
                </TabPanel>

                {/* Check Lists Content Goes Here */}
                <TabPanel>
                  <Box mx='-4' mt={1}>
                    <VStack spacing={6} align='stretch'>
                      {/* Assigned Checklists Section */}
                      <AssignedChecklists
                        assignedChecklists={assignedChecklists}
                        job={job}
                        setActiveChecklist={setActiveChecklist}
                        setChecklistModalOpen={setChecklistModalOpen}
                        setAssignedChecklists={setAssignedChecklists}
                      />
                      <Divider />
                      {/* Assign New Checklists Section */}
                      <Button alignSelf='center' colorScheme='blue' onClick={() => setAssignChecklistsModalOpen(true)}>
                        Assign Checklists
                      </Button>
                    </VStack>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        )
      )}

      <Modal
        isOpen={isJobLogModalOpen}
        onClose={() => {
          setEditingLog(null);
          onJobLogModalClose();
        }}
        size='4xl'
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingLog ? "Edit Job Log Entry" : "Add Job Log Entry"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <JobLogForm
              job={job}
              existingLog={editingLog}
              isModal={true}
              onSuccess={() => {
                setEditingLog(null);
                onJobLogModalClose();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Assign Checklists Modal */}
      <Modal isOpen={assignChecklistsModalOpen} onClose={() => setAssignChecklistsModalOpen(false)} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Checklists</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AssignChecklists
              checklists={checklists}
              assignedChecklists={assignedChecklists}
              job={job}
              setAssignedChecklists={setAssignedChecklists}
              onClose={() => setAssignChecklistsModalOpen(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Checklist Modal */}
      <ChecklistModal
        isOpen={checklistModalOpen}
        onClose={() => setChecklistModalOpen(false)}
        activeChecklist={activeChecklist}
        job={job}
        setAssignedChecklists={setAssignedChecklists}
      />
    </>
  );
};
export default JobScreen;
